/**
 * How new work is marked on a screen somebody leaves open.
 *
 * What counts as new is decided on the server, from a watermark stored per
 * administrator - see orders/watermark.ts. Doing it in the browser meant a
 * reload lit everything up again, and anything that arrived overnight was
 * never flagged because nobody had a tab open at the time.
 */

/**
 * The highlight itself: a wash of colour that fades out on its own.
 *
 * Animated rather than simply coloured, so it reads as "this just happened"
 * instead of "this row is special" - and it leaves the row looking like every
 * other one once it has been seen.
 */
export const flashSx = {
  '@keyframes arrivalFlash': {
    '0%': { backgroundColor: 'rgba(46, 125, 50, 0.55)' },
    '60%': { backgroundColor: 'rgba(46, 125, 50, 0.40)' },
    '100%': { backgroundColor: 'transparent' }
  },
  animation: 'arrivalFlash 8s ease-out forwards',
  // Someone who has asked for less motion still gets the colour, without the
  // slow fade drawing the eye back repeatedly.
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    backgroundColor: 'rgba(46, 125, 50, 0.28)'
  }
} as const;

/**
 * The standing mark on an order nobody here has opened yet.
 *
 * The flash says "this just arrived" and fades; this says "you have still not
 * looked at this", and stays until somebody does. Together they distinguish an
 * order that appeared while you watched from one that has been waiting since
 * yesterday - which the fading highlight alone could not.
 */
export const unseenSx = {
  borderLeft: '4px solid',
  borderLeftColor: 'success.main'
} as const;

/** How often a screen left open asks for new work. */
export const POLL_MS = 30_000;
